// shop-frontend/src/components/MallCartDrawer.jsx
import React from "react";
import { useShopMallCart } from "../context/ShopMallCartContext";
import { toAbsUrl } from "../utils/api";
import { X, Plus, Minus, Trash2, ShoppingBag, Package } from "lucide-react";

export default function MallCartDrawer({ theme, onClose, isOpen }) {
  const { items, count, total, updateQuantity, removeFromCart, clearCart } = useShopMallCart();

  if (!isOpen) return null;

  function money(n) {
    const x = Number(n);
    if (!Number.isFinite(x)) return "0.00";
    return x.toFixed(2);
  }

  function getUserAppBase() {
    const envBase =
      process.env.REACT_APP_USER_APP_URL ||
      process.env.REACT_APP_USER_FRONTEND_URL ||
      "";

    const cleanedEnv = String(envBase || "").trim().replace(/\/$/, "");
    if (cleanedEnv) return cleanedEnv;

    const host = String(window.location.host || "");
    if (host.startsWith("shop.")) {
      return `${window.location.protocol}//${host.replace(/^shop\./, "")}`;
    }

    return "http://localhost:5173";
  }

  function openUserCheckout() {
    const first = items?.[0] || null;
    const productId = String(first?.productId || first?._id || "").trim();
    const qty = Math.max(1, Number(first?.qty || 1));

    if (!productId) {
      window.alert("This cart item cannot be checked out right now.");
      return;
    }

    if ((items?.length || 0) > 1) {
      window.alert("Shop cart checkout opens one item at a time in the User app. Complete the first item, then return for the rest.");
    }

    const base = getUserAppBase();
    const url = `${base}/checkout/${encodeURIComponent(productId)}?qty=${qty}&fromCart=1&source=shop-cart`;
    window.location.assign(url);
  }

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0, 0, 0, 0.5)",
          zIndex: 998,
          backdropFilter: "blur(4px)",
        }}
      />

      {/* Drawer */}
      <div
        style={{
          position: "fixed",
          top: 0,
          right: 0,
          bottom: 0,
          width: "min(400px, 90vw)",
          background: theme.bg,
          borderLeft: `1px solid ${theme.border}`,
          zIndex: 999,
          display: "flex",
          flexDirection: "column",
          fontFamily: theme.font,
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: "20px",
            borderBottom: `1px solid ${theme.border}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            background: theme.card,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <ShoppingBag size={20} style={{ color: theme.primary }} />
            <h2 style={{ fontSize: 18, fontWeight: 700, color: theme.text }}>
              Mall Cart ({count})
            </h2>
          </div>
          <button
            onClick={onClose}
            style={{
              background: "transparent",
              border: "none",
              cursor: "pointer",
              padding: 8,
              borderRadius: 8,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: theme.text,
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = theme.primary + "15"}
            onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
          >
            <X size={20} />
          </button>
        </div>

        {/* Cart Items */}
        <div style={{ flex: 1, overflowY: "auto", padding: "16px" }}>
          {items.length === 0 ? (
            <div
              style={{
                textAlign: "center",
                padding: "60px 20px",
                color: theme.muted,
              }}
            >
              <Package size={48} style={{ margin: "0 auto 16px", opacity: 0.3 }} />
              <p style={{ fontSize: 14 }}>Your mall cart is empty</p>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              {items.map((item) => {
                const cartId = item.cartId || item.productId || item._id;
                const qty = Math.max(1, Number(item.qty) || 1);
                const price = Number(item.price) || 0;
                const itemTotal = price * qty;

                return (
                  <div
                    key={cartId}
                    style={{
                      background: theme.card,
                      border: `1px solid ${theme.border}`,
                      borderRadius: 12,
                      padding: 12,
                      display: "flex",
                      gap: 12,
                    }}
                  >
                    {/* Product Image */}
                    <div
                      style={{
                        width: 80,
                        height: 80,
                        borderRadius: 8,
                        overflow: "hidden",
                        flexShrink: 0,
                        background: theme.bg,
                      }}
                    >
                      {item.image ? (
                        <img
                          src={toAbsUrl(item.image)}
                          alt={item.title}
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                        />
                      ) : (
                        <div
                          style={{
                            width: "100%",
                            height: "100%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            background: theme.primary + "10",
                          }}
                        >
                          <ShoppingBag size={24} style={{ color: theme.muted }} />
                        </div>
                      )}
                    </div>

                    {/* Product Info */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <h3
                        style={{
                          fontSize: 14,
                          fontWeight: 600,
                          color: theme.text,
                          marginBottom: 2,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {item.title}
                      </h3>
                      {item.variantLabel ? (
                        <p style={{ fontSize: 11, color: theme.accent || theme.primary, marginBottom: 2, fontWeight: 500 }}>
                          {item.variantLabel}
                        </p>
                      ) : null}
                      <p style={{ fontSize: 11, color: theme.muted, marginBottom: 6 }}>
                        {item.shopName || "Shop"}
                      </p>
                      <p style={{ fontSize: 14, fontWeight: 700, color: theme.primary }}>
                        {item.currency || "USD"} {money(itemTotal)}
                        {qty > 1 && (
                          <span style={{ fontSize: 11, fontWeight: 400, color: theme.muted, marginLeft: 6 }}>
                            ({item.currency || "USD"} {money(price)} each)
                          </span>
                        )}
                      </p>

                      {/* Quantity Controls */}
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          marginTop: 12,
                        }}
                      >
                        <button
                          onClick={() => updateQuantity(cartId, qty - 1)}
                          style={{
                            width: 28,
                            height: 28,
                            background: theme.bg,
                            border: `1px solid ${theme.border}`,
                            borderRadius: 6,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            cursor: "pointer",
                            color: qty <= 1 ? "#ef4444" : theme.text,
                          }}
                          title={qty <= 1 ? "Remove item" : "Decrease quantity"}
                        >
                          {qty <= 1 ? <Trash2 size={14} /> : <Minus size={14} />}
                        </button>
                        <span
                          style={{
                            fontSize: 14,
                            fontWeight: 600,
                            color: theme.text,
                            minWidth: 24,
                            textAlign: "center",
                          }}
                        >
                          {qty}
                        </span>
                        <button
                          onClick={() => updateQuantity(cartId, qty + 1)}
                          style={{
                            width: 28,
                            height: 28,
                            background: theme.bg,
                            border: `1px solid ${theme.border}`,
                            borderRadius: 6,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            cursor: "pointer",
                            color: theme.text,
                          }}
                        >
                          <Plus size={14} />
                        </button>
                        <div style={{ flex: 1 }} />
                        <button
                          onClick={() => removeFromCart(cartId)}
                          style={{
                            background: "transparent",
                            border: "none",
                            cursor: "pointer",
                            padding: 6,
                            color: "#ef4444",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}

              {/* Clear Cart Button */}
              {items.length > 0 && (
                <button
                  onClick={() => {
                    clearCart();
                  }}
                  style={{
                    background: "transparent",
                    border: `1px solid ${theme.border}`,
                    borderRadius: 8,
                    padding: "12px 16px",
                    color: theme.muted,
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 8,
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = "#ef4444";
                    e.currentTarget.style.color = "#ef4444";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = theme.border;
                    e.currentTarget.style.color = theme.muted;
                  }}
                >
                  <Trash2 size={14} />
                  Clear Cart
                </button>
              )}
            </div>
          )}
        </div>

        {/* Footer (Checkout Summary) */}
        {items.length > 0 && (
          <div
            style={{
              borderTop: `1px solid ${theme.border}`,
              padding: "20px",
              background: theme.card,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: 16,
              }}
            >
              <span style={{ fontSize: 14, color: theme.muted }}>Subtotal</span>
              <span style={{ fontSize: 18, fontWeight: 700, color: theme.text }}>
                USD {money(total)}
              </span>
            </div>
            <button
              onClick={openUserCheckout}
              style={{
                width: "100%",
                background: theme.primary,
                color: "#fff",
                padding: "14px 24px",
                borderRadius: 10,
                border: "none",
                fontSize: 15,
                fontWeight: 700,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = theme.primaryHover}
              onMouseLeave={(e) => e.currentTarget.style.background = theme.primary}
            >
              <ShoppingBag size={18} />
              Checkout ({count} items)
            </button>
            <p
              style={{
                fontSize: 11,
                color: theme.muted,
                textAlign: "center",
                marginTop: 12,
              }}
            >
              Shipping & taxes calculated at checkout
            </p>
          </div>
        )}
      </div>
    </>
  );
}
